"use client";
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../config/apiConfig";

const WorkContext = createContext();

// CHANGED: Added a safe JSON parsing helper to prevent "Unexpected token '<'" crashes.
// This guards against cases where the backend returns an HTML error page (404/500/proxy errors)
// or an empty body instead of valid JSON, which previously caused res.json() to throw.
const safeParseJSON = async (res) => {
  try {
    const text = await res.text();
    if (!text) {
      return { status: res.status, message: "Empty response from server" };
    }
    try {
      return JSON.parse(text);
    } catch (parseErr) {
      return { status: res.status, message: "Invalid response format from server" };
    }
  } catch (readErr) {
    return { status: res.status || 500, message: "Failed to read server response" };
  }
};

export const WorkProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(false);

  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [completedProjects, setCompletedProjects] = useState([]);
  const [employeeNames, setEmployeeNames] = useState([]);
  const [week, setWeek] = useState(null);
  const [employees, setEmployees] = useState([]);

  // get token from localStorage
  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    if (storedToken) setToken(storedToken);
  }, []);

  // Get all employee names (for dropdown / HR use) — fetch operation, no toasts
  // CHANGED: Removed toast.error calls — this is a GET/fetch operation, not a user action.
  const fetchEmployeeNames = async () => {
    if (!token) return;

    try {
      setLoading(true);

      const res = await fetch(`${BASE_URL}/allemployees`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()
      if (data.status === 200) {
        setEmployeeNames(data.data || []);
      } else {
        console.warn("⚠️ Failed to fetch employee names:", data.message); // CHANGED: replaced toast.error with console.warn
      }
    } catch (err) {
      console.error("Error fetching employee names:", err);
      // CHANGED: removed toast.error("Error fetching employee names")
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchEmployeeNames();
    }
  }, [token]);

  // existing employee dashboard — fetch operation, no toasts
  // CHANGED: Removed toast.error calls — this is a GET/fetch operation, not a user action.
  const fetchEmployee = async (employeeId) => {
    if (!token) return;
    try {
      setLoading(true);
      const res = await fetch(
        `${BASE_URL}/work-dashboard/${employeeId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()
      if (data.status === 200) {
        setEmployee(data.employee || null);
        setTasks(data.tasks || []);
      } else {
        console.warn("⚠️ Failed to fetch employee dashboard:", data.message); // CHANGED: replaced toast.error with console.warn
      }
    } catch (err) {
      console.error("Error fetching employee data:", err);
      // CHANGED: removed toast.error("Error fetching employee data")
    } finally {
      setLoading(false);
    }
  };

  // HR dashboard API — fetch operation, no toasts
  // CHANGED: Removed toast.error calls — this is a GET/fetch operation, not a user action.
  const fetchHRDashboard = async (employeeId) => {
    if (!token) return;
    try {
      setLoading(true);
      const res = await fetch(
        `${BASE_URL}/hr/work-dashboard/${employeeId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()

      if (data.status === 200) {
        setWeek(data.week || null);
        setEmployee(data.employee || null);
        setTasks(data.tasks || []);
        setProjects(data.projects || []);
        setCompletedProjects(data.completed_projects || []);
      } else {
        console.warn("⚠️ Failed to fetch HR dashboard:", data.message); // CHANGED: replaced toast.error with console.warn
      }
    } catch (err) {
      console.error("Error fetching HR dashboard:", err);
      // CHANGED: removed toast.error("Error fetching HR dashboard")
    } finally {
      setLoading(false);
    }
  };

  // Update (PUT) — user action, toasts preserved
  const updateProgressAPI = async (projectId, newProgress) => {
    try {
      const response = await fetch(
        `${BASE_URL}/tasks/${projectId}/progress`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ progress: newProgress }),
        }
      );

      if (!response.ok) throw new Error("Failed to update progress");

      const data = await safeParseJSON(response); // CHANGED: safe parsing instead of response.json()
      toast.success(data.message || "Progress updated successfully"); // KEPT: user-initiated action
      return data;
    } catch (error) {
      console.error("Error updating progress:", error);
      toast.error(error.message || "Failed to update progress"); // KEPT: user-initiated action
      return null;
    }
  };

  // Update (PUT) — user action, toasts preserved
  const updateTaskDescription = async (taskId, description) => {
    try {
      const res = await fetch(`${BASE_URL}/tasks/${taskId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ description }),
      });

      if (!res.ok) throw new Error("Failed to update task");

      const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()
      toast.success(data.message || "Task updated successfully"); // KEPT: user-initiated action
      return data;
    } catch (err) {
      console.error("Update task error:", err);
      toast.error(err.message || "Failed to update task"); // KEPT: user-initiated action
      return null;
    }
  };

  // All employees dashboard — fetch operation, no toasts
  // CHANGED: Removed toast.error calls — this is a GET/fetch operation, not a user action.
  const fetchAllEmployeesDashboard = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);

      const res = await fetch(
        `${BASE_URL}/work-dashboard`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()

      if (data.status === 200) {
        setWeek(data.week || null);
        setEmployees(data.employees || []);
        setProjects(data.projects || []);
        setCompletedProjects(data.completed_projects || []);
      } else {
        console.warn("⚠️ Failed to fetch employees dashboard:", data.message); // CHANGED: replaced toast.error with console.warn
      }
    } catch (err) {
      console.error("Error fetching all employees dashboard:", err);
      // CHANGED: removed toast.error("Error fetching all employees dashboard")
    } finally {
      setLoading(false);
    }
  }, [token]);

  return (
    <WorkContext.Provider
      value={{
        token,
        loading,
        employee,
        employees,
        updateTaskDescription,
        setTasks,
        tasks,
        projects,
        completedProjects,
        week,
        fetchEmployee,
        fetchHRDashboard,
        fetchAllEmployeesDashboard,
        updateProgressAPI,
        employeeNames,
        fetchEmployeeNames,
      }}
    >
      {children}
    </WorkContext.Provider>
  );
};

export const useWork = () => useContext(WorkContext);